import React, { useState } from 'react';
import { Form, Grid, Input, Select, Typography } from 'antd';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';

const { useBreakpoint } = Grid;

interface FormItemSelectGroupProps {
  name: string;
  title: string;
  subjects: { name: string; title: string }[];
  options: { value: number; label: string }[];
  validators: {
    validator?: (
      _: unknown,
      value: Record<string, number | string>
    ) => void | Promise<void>;
    message?: string;
  }[];
  hasOther?: boolean;
  maxCategories: number;
  onChange: () => void;
  t: TFunction;
}

export const FormItemSelectGroup = (
  props: FormItemSelectGroupProps
): JSX.Element => {
  const {
    name,
    title,
    subjects,
    options,
    validators,
    hasOther,
    maxCategories,
    onChange,
    t,
  } = props;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const form = Form.useFormInstance();
  const screens = useBreakpoint();

  const handleValueChange = async (
    fieldName: string,
    value: number | string
  ): Promise<void> => {
    const currentValues = form.getFieldValue(name) || {};
    const newValues: Record<string, number> = {
      ...currentValues,
      [fieldName]: value,
    };

    setSelectedCount(Object.values(newValues).filter((v) => v > 0).length);

    form.setFieldValue(name, newValues);

    let hasError = false;

    for (const v of validators) {
      if (v.validator) {
        try {
          await v.validator({}, newValues);
          // no error
          setErrorMessage(null);
        } catch (err: unknown) {
          // show red warning text
          hasError = true;
          if (err instanceof Error) {
            setErrorMessage(v.message || err.message);
          } else {
            setErrorMessage(v.message || t('evaluationComp.invalidInput'));
          }
          break; // stop after first error
        }
      }
    }

    if (!hasError) {
      form.setFields([{ name, errors: [] }]);
    }

    onChange();
  };

  const isOtherDisabled: boolean =
    selectedCount >= maxCategories && !form.getFieldValue([name, 'otherValue']);

  const subjectDropdowns: React.ReactNode[] = subjects.map((subject) => {
    const isDropdownDisabled =
      selectedCount >= maxCategories &&
      !form.getFieldValue([name, subject.name]);
    return (
      <Form.Item
        initialValue={0}
        key={`${name}.${subject.name}`}
        name={[name, subject.name]}
        label={
          <span style={{ whiteSpace: 'normal', lineHeight: '1.2' }}>
            {subject.title}
          </span>
        }
        colon={false}
        labelCol={{ style: { paddingBottom: screens.xs ? 2 : 0 }, span: 16 }}
        wrapperCol={{ span: 4 }}
        style={{ marginBottom: screens.xs ? 30 : 2 }}
      >
        <Select
          style={{ width: '100%' }}
          options={options}
          disabled={isDropdownDisabled}
          onChange={(value) => handleValueChange(subject.name, value)}
        />
      </Form.Item>
    );
  });

  return (
    <div style={{ marginBottom: '20px' }}>
      <Typography.Title level={4}>{title}</Typography.Title>
      <span style={{ color: 'red' }}>
        {t('evaluationComp.mustSelect')} {maxCategories}
      </span>

      {subjectDropdowns}

      {hasOther && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Form.Item
            key={`${name}.otherTitle`}
            name={[name, 'otherTitle']}
            style={{ marginBottom: 0, marginRight: '10px' }}
          >
            <Input
              placeholder={t('evalReportDisplay.other')}
              style={{ maxWidth: 200 }}
              onChange={(e) => handleValueChange('otherTitle', e.target.value)}
            />
          </Form.Item>
          <Form.Item
            initialValue={0}
            key={`${name}.otherValue`}
            name={[name, 'otherValue']}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ minWidth: 150, maxWidth: 200 }}
              options={options}
              disabled={isOtherDisabled}
              onChange={(value) => handleValueChange('otherValue', value)}
            />
          </Form.Item>
        </div>
      )}

      {errorMessage && (
        <div style={{ color: 'red', marginTop: 8, fontSize: 13 }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};
